import Api from './api';

/**
 * P4P Day reads for the web client.
 *
 * Enrolment is NOT here. Only the mobile app claims badges — the campaign hands a physical gift
 * over at a stadium booth, and a fan signing in to the CMS is not at the stadium. The server
 * refuses a web enrolment outright, so this is a real gate and not just a client-side omission.
 *
 * The web still DISPLAYS a badge the app awarded: the profile header, the fan gift modal and
 * the admin booth panel all read through the functions below.
 */

/**
 * The campaign's public status, fetched once per page load.
 *
 * Cached in a module-level promise because the header renders on most profile views and this
 * must not become a request per mount. Safe to cache for a whole session: nothing it carries
 * moves during one. The number on a fan's pennant is their own `p4pDay.badgeNo`, fixed at claim
 * time, not a live stock count, and the response deliberately publishes no per-colour stock.
 */
let statusPromise = null;

function getP4PDayStatus() {
  if (!statusPromise) {
    statusPromise = Api.get('/p4pday/status', null, { silent: true })
      .then((res) => res?.data || null)
      .catch(() => {
        // Let the next caller retry rather than caching a failure for the whole session.
        statusPromise = null;
        return null;
      });
  }
  return statusPromise;
}

/**
 * Badge palette: letter -> name, colour and gift item.
 *
 * The user record stores only the letter, so this is the read that lets the sponsor rename or
 * recolour every issued badge from the campaign config, with no deploy.
 */
export function getP4PDayBadges() {
  return getP4PDayStatus().then((status) => status?.badges || []);
}

/**
 * The team channel the campaign belongs to — the only profile the badge is shown on.
 *
 * Read from the campaign rather than from `REACT_APP_DEFAULT_TEAM_USERNAME`, because the server
 * resolved this same value to the team it enrolled and auto-followed fans to. Comparing against
 * the client env var instead would let the badge appear beside a team the campaign never
 * involved whenever the two drift, and that failure is silent.
 */
export function getP4PDayTeamUsername() {
  return getP4PDayStatus().then((status) => status?.teamUsername || null);
}

/**
 * The signed-in fan's own enrolment — fan ID, badge, and whether the gift has been handed over.
 *
 * Never cached, unlike the badge palette above. The fan opens this standing at the booth while
 * staff mark the handover on another device; a cached `giftClaimed: false` would have them
 * arguing they were never given their gift.
 */
export async function getP4PDaySelfEnrolment() {
  const res = await Api.get('/p4pday/self', null, { silent: true });
  return res?.data || null;
}
